/**
 * Shortest Path API Service - JavaScript Version
 * 
 * Handles all API communication with the backend shortest path module.
 * Provides a clean interface for algorithm execution and data retrieval.
 */

import axios from 'axios';

// API base URL - adjust based on your backend configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * API client configuration
 */
const apiConfig = {
  baseURL: `${API_BASE_URL}/api/shortest-path`,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
};

const apiClient = axios.create(apiConfig);

/**
 * Error handler for API requests
 */
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    console.error('API Error Response:', {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      headers: error.response.headers
    });
    
    const responseData = error.response.data;
    const message = responseData?.detail || 
                   responseData?.message || 
                   responseData?.error ||
                   `Server error: ${error.response.status} ${error.response.statusText}`;
    
    return {
      message: message,
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data
    };
  } else if (error.request) {
    // Request was made but no response received
    console.error('API Request Error:', error.request);
    return {
      message: 'Network error: No response from server',
      status: 0,
      statusText: 'No Response',
      data: null
    };
  } else {
    // Something else happened
    console.error('API Setup Error:', error.message);
    return {
      message: `Request setup error: ${error.message}`,
      status: 0,
      statusText: 'Setup Error',
      data: null
    };
  }
};

/**
 * Calculate shortest path using specified algorithm
 */
export const calculateShortestPath = async (requestData) => {
  try {
    // Round coordinates to integers as required by the backend API
    const processedRequest = {
      ...requestData,
      nodes: requestData.nodes.map(node => ({
        ...node,
        x: Math.round(node.x || 0),
        y: Math.round(node.y || 0)
      }))
    };
    
    console.log('Sending shortest path request:', processedRequest);
    
    const response = await apiClient.post('/calculate', processedRequest);
    
    console.log('Shortest path response received:', response.data);
    
    // Return the response data directly, not wrapped in success/data structure
    return response.data;
  } catch (error) {
    console.error('Shortest path calculation failed:', error);
    const apiError = handleApiError(error);
    
    // Return error structure that matches what useAlgorithm expects
    return {
      success: false,
      error: apiError.message,
      path: [],
      cost: -1,
      steps: [],
      algorithm: requestData.algorithm,
      executionTime: 0
    };
  }
};

/**
 * Get available algorithms
 */
export const getAvailableAlgorithms = async () => {
  try {
    console.log('Fetching available algorithms...');
    
    const response = await apiClient.get('/algorithms');
    
    console.log('Available algorithms response:', response.data);
    
    // Return just the algorithms array from the response
    return response.data.algorithms || [];
  } catch (error) {
    console.error('Failed to fetch algorithms:', error);
    throw error; // Re-throw to be caught by the calling component
  }
};

/**
 * Health check endpoint
 */
export const healthCheck = async () => {
  try {
    console.log('Performing health check...');
    
    const response = await apiClient.get('/health');
    
    console.log('Health check response:', response.data);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Health check failed:', error);
    const apiError = handleApiError(error);
    
    return {
      success: false,
      error: apiError
    };
  }
};

/**
 * Get sample graphs from backend
 */
export const getSampleGraphs = async () => {
  try {
    console.log('Fetching sample graphs...');
    
    const response = await apiClient.get('/sample-graphs');
    
    console.log('Sample graphs response received:', response.data);
    return response.data;
  } catch (error) {
    const apiError = handleApiError(error);
    console.error('Failed to fetch sample graphs:', apiError);
    throw new Error(apiError.message);
  }
};

/**
 * Default export for the API service
 */
const APIService = {
  calculateShortestPath,
  getAvailableAlgorithms,
  healthCheck,
  getSampleGraphs
};

export default APIService;
